import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import SegmentedControl from "./SegmentedControl";
import FeatureToggleList from "./FeatureToggleList";
import ColumnToggleList from "./ColumnToggleList";
import { PAGE_STATES } from "../hooks/useUserPermissionConfig";

const STATE_HINT_KEY = {
  [PAGE_STATES.HIDDEN]: "stateHiddenHint",
  [PAGE_STATES.LOCKED]: "stateLockedHint",
  [PAGE_STATES.FULL]: "stateFullHint",
};

/**
 * One registry page: label + 3-state visibility control; when Full Access,
 * an expandable body with feature switches and table-column chips.
 * Feature/column toggles only apply while the page is VISIBLE_ACCESSIBLE —
 * in the Locked state they are irrelevant, so the body stays collapsed.
 */
const PageCard = ({
  page, state, enabledFeatures, hiddenColumns,
  onSetPageState, onToggleFeature, onToggleColumn,
  theme, language, t,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const effectiveState = state || PAGE_STATES.HIDDEN; // no row = hidden
  const isFull = effectiveState === PAGE_STATES.FULL;
  const hasDetails = (page.features?.length || 0) + (page.tables?.length || 0) > 0;
  const showBody = isFull && hasDetails && expanded;

  const enabledCount = Object.keys(enabledFeatures || {}).length;
  const hiddenColCount = Object.keys(hiddenColumns || {}).length;

  return (
    <div
      style={{
        borderRadius: "14px",
        border: theme === "dark"
          ? "1px solid rgba(148,163,184,0.14)"
          : "1px solid rgba(15,23,42,0.07)",
        background: theme === "dark" ? "rgba(30,41,59,0.5)" : "rgba(255,255,255,0.85)",
        opacity: effectiveState === PAGE_STATES.HIDDEN ? 0.72 : 1,
        transition: "opacity 0.25s ease, border-color 0.25s ease",
        overflow: "hidden",
      }}
    >
      {/* header row */}
      <div
        onClick={() => { if (isFull && hasDetails) setExpanded((v) => !v); }}
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          padding: "14px 16px",
          cursor: isFull && hasDetails ? "pointer" : "default",
        }}
      >
        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
          <div style={{
            fontSize: "14.5px", fontWeight: 700,
            color: theme === "dark" ? "#f1f5f9" : "#1e293b",
          }}>
            {page.labels?.[language] || page.labels?.en || page.key}
          </div>
          {isFull && (enabledCount > 0 || hiddenColCount > 0) && (
            <div style={{
              fontSize: "11.5px", marginTop: "2px",
              color: theme === "dark" ? "#94a3b8" : "#64748b",
            }}>
              {enabledCount > 0 && `${enabledCount} ✓`}
              {enabledCount > 0 && hiddenColCount > 0 && " · "}
              {hiddenColCount > 0 && `${hiddenColCount} ⊘`}
            </div>
          )}
        </div>

        <SegmentedControl
          value={effectiveState}
          onChange={(next) => onSetPageState(page.key, next)}
          theme={theme}
          t={t}
          disabled={disabled}
        />

        {isFull && hasDetails && (
          <ChevronDown
            size={18}
            style={{
              color: theme === "dark" ? "#94a3b8" : "#64748b",
              transition: "transform 0.25s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* one-line helper for the current state (plain words for non-technical admins) */}
      <div style={{
        padding: "0 16px 12px",
        fontSize: "12px",
        color: theme === "dark" ? "#8ea0b8" : "#7c8aa0",
      }}>
        {t[STATE_HINT_KEY[effectiveState]]}
      </div>

      {/* expandable detail body — smooth grid-rows transition */}
      <div style={{
        display: "grid",
        gridTemplateRows: showBody ? "1fr" : "0fr",
        transition: "grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1)",
      }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{
            padding: "16px",
            borderTop: theme === "dark"
              ? "1px solid rgba(148,163,184,0.1)"
              : "1px solid rgba(15,23,42,0.05)",
            display: "flex", flexDirection: "column", gap: "20px",
          }}>
            {!page.features?.length && !page.tables?.length ? (
              <div style={{ fontSize: "12.5px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {t.noFeaturesForPage}
              </div>
            ) : (
              <>
                <FeatureToggleList
                  page={page}
                  enabledFeatures={enabledFeatures}
                  onToggleFeature={onToggleFeature}
                  theme={theme} language={language} t={t}
                  disabled={disabled}
                />
                <ColumnToggleList
                  page={page}
                  hiddenColumns={hiddenColumns}
                  onToggleColumn={onToggleColumn}
                  theme={theme} language={language} t={t}
                  disabled={disabled}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageCard;
